import { Injectable, Inject } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { ContainerClient, AnonymousCredential } from '@azure/storage-blob'
import { Subject, firstValueFrom } from 'rxjs'
import { SasTokenManager, SasTokenResponse } from '../utils/sas-token-manager'
import { WarningDatasources } from '../enums/warning-types.enum'
import { AuthService } from 'src/app/core/services/auth.service'
import { APP_CONFIG } from 'src/app/app.config'
import { BlobItem, BlobListResult, BlobPrefix } from '../models/blob-storage.model'

@Injectable()
export class BlobStorageService {
  private containerClients: Map<WarningDatasources, ContainerClient> = new Map()
  private destroy$ = new Subject<void>()
  private tokenManagers: Map<WarningDatasources, SasTokenManager> = new Map()
  private allowedDatasources: WarningDatasources[] = []
  private tokensReady$ = new Subject<void>()
  private isRefreshing = false
  private blobCache: Map<WarningDatasources, BlobItem[]> = new Map()

  constructor(
    public authService: AuthService,
    private http: HttpClient,
    @Inject(APP_CONFIG) private config: any,
  ) {
    this.allowedDatasources = this.authService.getUserClaims().userDataFilters

    // Initialize a SasTokenManager for each allowed datasource WITHOUT auto-refresh
    // Tokens will be refreshed on-demand when expired before actual use
    for (const datasource of this.allowedDatasources) {
      const manager = new SasTokenManager() // No refresh callback
      this.tokenManagers.set(datasource, manager)
    }

    // Load tokens and initial cache from backend on initialization
    this.loadSasTokens(true)
  }

  /**
   * Wait for tokens to be loaded from backend.
   * Resolves immediately if tokens are already loaded.
   */
  private waitForTokensLoaded(): Promise<void> {
    return new Promise((resolve) => {
      const allTokensLoaded = this.allowedDatasources.every((ds) =>
        this.tokenManagers.get(ds)?.getToken(ds),
      )
      if (allTokensLoaded) {
        resolve()
      } else {
        const subscription = this.tokensReady$.subscribe(() => {
          resolve()
          subscription.unsubscribe()
        })
      }
    })
  }

  /**
   * Initialize blob container clients for all allowed datasources.
   */
  private initializeAllClients(forceRebuild = false): void {
    if (forceRebuild) {
      this.containerClients.clear()
    }

    for (const datasource of this.allowedDatasources) {
      const manager = this.tokenManagers.get(datasource)
      const token = manager?.getToken(datasource)
      if (!token) {
        console.warn('No SAS token available yet for datasource:', datasource)
        continue
      }

      // Get container URL from token manager
      const containerUrl = manager?.getTokenEntry?.(datasource)?.containerUrl
      if (!containerUrl) {
        console.warn('No container URL available for datasource:', datasource)
        continue
      }

      const containerClient = new ContainerClient(
        `${containerUrl}?${token}`,
        new AnonymousCredential(),
      )
      this.containerClients.set(datasource, containerClient)
    }
  }

  /**
   * Get the container client for a specific datasource.
   */
  private getContainerClient(datasource: WarningDatasources): ContainerClient | null {
    return this.containerClients.get(datasource) || null
  }

  /**
   * Set a SAS token for a specific datasource (internal use).
   */
  private setSasTokenForDatasource(
    datasource: WarningDatasources,
    token: string,
    expiresAt: Date,
    containerUrl: string,
  ): void {
    const manager = this.tokenManagers.get(datasource)
    if (!manager) {
      console.error('No token manager for datasource:', datasource)
      return
    }

    manager.setToken(datasource, {
      token,
      expiresAt,
      containerUrl,
    })
  }

  /**
   * Load SAS tokens for one or all allowed datasources from the backend.
   * The backend returns a map of datasource -> full URL with embedded SAS token.
   * Backend endpoint: GET <backend_url>/rcs/historic/sas-token?dataFilters=<ds1>&dataFilters=<ds2>...
   * Only one refresh is allowed at a time to prevent concurrent calls.
   */
  private async loadSasTokens(
    reloadCache = false,
    datasource: WarningDatasources | undefined = undefined,
  ): Promise<void> {
    // Prevent concurrent token refresh calls
    if (this.isRefreshing) {
      // console.log('Token refresh already in progress, skipping...')
      return
    }

    this.isRefreshing = true

    try {
      const accessToken = await this.authService.getAccessToken()

      // Build query string with all dataFilters
      const params = new URLSearchParams()
      if (!datasource) {
        for (const ds of this.allowedDatasources) {
          params.append('dataFilters', ds)
        }
      } else {
        params.append('dataFilters', datasource)
      }

      const response = await firstValueFrom(
        this.http.get<SasTokenResponse>(
          `${this.config.backend_url}rcs/historic/sas-token?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        ),
      )

      // Parse response and update token managers
      for (const [datasource, { token, expiresAt, containerUrl }] of Object.entries(response)) {
        this.setSasTokenForDatasource(
          datasource as WarningDatasources,
          token,
          new Date(expiresAt),
          containerUrl,
        )
      }

      // Initialize clients for all datasources now that tokens are loaded
      this.initializeAllClients(true)

      // Only build cache on initial load (avoid re-listing on refresh)
      if (reloadCache) {
        this.blobCache.clear()
        await this.loadAllBlobsForAllDatasources()
      }

      // Notify that tokens are ready
      this.tokensReady$.next()

      // if (!datasource) {
      //   console.log('SAS tokens loaded successfully for all datasources')
      // } else {
      //   console.log(`SAS token loaded successfully for datasource: ${datasource}`)
      // }
    } catch (error) {
      console.error('Failed to load SAS tokens:', error)
    } finally {
      this.isRefreshing = false
    }
  }

  /**
   * Load all blobs for all allowed datasources into cache.
   */
  private async loadAllBlobsForAllDatasources(): Promise<void> {
    for (const datasource of this.allowedDatasources) {
      await this.loadAllBlobsForDatasource(datasource)
    }
  }

  /**
   * Load all blobs for a single datasource into cache.
   */
  private async loadAllBlobsForDatasource(datasource: WarningDatasources): Promise<void> {
    const containerClient = this.getContainerClient(datasource)
    if (!containerClient) {
      return
    }

    const blobs: BlobItem[] = []
    try {
      for await (const item of containerClient.listBlobsFlat()) {
        const properties = item.properties
        blobs.push({
          name: item.name,
          displayName: item.name.split('/').pop() || item.name,
          contentLength: properties.contentLength || 0,
          contentType: properties.contentType || '',
        })
      }

      this.blobCache.set(datasource, blobs)
    } catch (error) {
      console.error(`Failed to load blobs for datasource ${datasource}:`, error)
    }
  }

  /**
   * List blobs with hierarchical folder structure for all datasources.
   * At root level (no prefix), shows all datasources as virtual folders.
   * When prefix matches a datasource, lists blobs from that datasource.
   * Supports nested navigation like "warnings.bmw/subfolder/"
   *
   * @param prefix Optional path. Can be:
   *   - undefined or '' (root): shows all datasources as folders
   *   - 'warnings.bmw': shows root blobs of BMW datasource
   *   - 'warnings.bmw/folder/': shows blobs in that subfolder
   */
  async listBlobs(prefix?: string): Promise<BlobListResult> {
    // Root level does not require token validation
    if (!prefix || prefix === '') {
      await this.waitForTokensLoaded()

      const blobs: BlobItem[] = []
      const prefixes: BlobPrefix[] = []

      for (const datasource of this.allowedDatasources) {
        prefixes.push({
          name: datasource,
          displayName: datasource.replace('warnings.', '').replace(/_/g, '-'),
        })
      }
      return { blobs, prefixes }
    }

    // Determine which datasource and sub-path from the prefix
    const datasource = this.extractDatasource(prefix)
    if (!datasource) {
      throw new Error(`Invalid prefix: ${prefix}`)
    }

    // Ensure token is valid before listing blobs in this datasource
    await this.ensureTokensValid(datasource)
    await this.waitForTokensLoaded()

    const blobs: BlobItem[] = []
    const prefixes: BlobPrefix[] = []

    // Extract the blob prefix (path within the container)
    const blobPrefix =
      prefix.length > datasource.length ? prefix.substring(datasource.length + 1) : ''

    // Ensure cache is loaded for this datasource
    if (!this.blobCache.has(datasource)) {
      await this.loadAllBlobsForDatasource(datasource)
    }

    const cachedBlobs = this.blobCache.get(datasource) || []
    const listing = this.buildListingFromCache(cachedBlobs, blobPrefix, datasource)
    prefixes.push(...listing.prefixes)
    blobs.push(...listing.blobs)

    return { blobs, prefixes }
  }

  /**
   * Build a hierarchical listing from cached blobs for a specific prefix.
   */
  private buildListingFromCache(
    blobs: BlobItem[],
    blobPrefix: string,
    datasource: WarningDatasources,
  ): BlobListResult {
    const prefixSet = new Set<string>()
    const resultBlobs: BlobItem[] = []

    for (const blob of blobs) {
      if (!blob.name.startsWith(blobPrefix)) {
        continue
      }

      const remaining = blob.name.substring(blobPrefix.length)
      const parts = remaining.split('/').filter(Boolean)

      if (parts.length > 1) {
        const folder = parts[0]
        prefixSet.add(folder)
      } else if (parts.length === 1) {
        resultBlobs.push({
          ...blob,
          name: `${datasource}/${blob.name}`,
        })
      }
    }

    const resultPrefixes: BlobPrefix[] = Array.from(prefixSet).map((folder) => ({
      name: `${datasource}/${blobPrefix}${folder}/`,
      displayName: folder,
    }))

    return { blobs: resultBlobs, prefixes: resultPrefixes }
  }

  /**
   * Extract datasource from a path.
   * E.g., 'warnings.bmw' or 'warnings.bmw/folder' -> 'warnings.bmw'
   */
  private extractDatasource(path: string): WarningDatasources | null {
    // Find the datasource that matches the start of the path
    for (const ds of this.allowedDatasources) {
      if (path === ds || path.startsWith(ds + '/')) {
        return ds
      }
    }
    return null
  }

  /**
   * Get a direct download URL for a blob (with SAS token).
   * @param blobName Full path including datasource, e.g., 'warnings.bmw/folder/file.json'
   */
  getDownloadUrl(blobName: string): string {
    // Extract datasource from blobName full path
    const datasource = this.extractDatasource(blobName)
    if (!datasource) {
      throw new Error(`Invalid blob path (no valid datasource): ${blobName}`)
    }

    const manager = this.tokenManagers.get(datasource)
    const token = manager?.getToken(datasource)
    if (!token) {
      throw new Error(`No SAS token available for datasource: ${datasource}`)
    }

    // Get container URL from token manager
    const containerUrl = manager?.getTokenEntry?.(datasource)?.containerUrl
    if (!containerUrl) {
      throw new Error(`No container URL available for datasource: ${datasource}`)
    }

    // Extract just the blob path (without datasource prefix)
    const blobPath = blobName.substring(datasource.length + 1)

    return `${containerUrl}/${encodeURIComponent(blobPath)}?${token}`
  }

  /**
   * Trigger a file download in the browser.
   * The file will be downloaded with its original filename.
   */
  async downloadFile(blobName: string): Promise<void> {
    // Ensure token is still valid before downloading
    const datasource = this.extractDatasource(blobName)
    if (datasource) {
      await this.ensureTokensValid(datasource)
    }
    await this.waitForTokensLoaded()

    const url = this.getDownloadUrl(blobName)
    const link = document.createElement('a')
    link.href = url
    // Extract just the filename (last part of the path)
    link.download = blobName.split('/').pop() || blobName
    // Append to DOM, click, then remove
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  private async ensureTokensValid(datasource: WarningDatasources): Promise<void> {
    const manager = this.tokenManagers.get(datasource)
    if (!manager) {
      return
    }

    // Skip refresh if no token exists for this datasource yet
    const entry = manager.getTokenEntry(datasource)
    if (!entry) {
      return
    }

    // Strict expiration check (no 60s buffer) for on-demand refresh
    if (entry.expiresAt.getTime() <= Date.now()) {
      // console.log(`Token expired for ${datasource}, refreshing...`)
      await this.loadSasTokens(false, datasource)
    }
  }

  destroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
    this.tokensReady$.complete()
    // Destroy all token managers
    for (const manager of this.tokenManagers.values()) {
      manager.destroy()
    }
    this.tokenManagers.clear()
    this.containerClients.clear()
  }
}
